// W A S D キーでキャンバスを pan する。
// 押下中フラグを保持し、requestAnimationFrame ループで pan 量を加算することで滑らかに動く。
// IME や input 要素にフォーカスがあるときは無効化する。

import { useEffect } from "react";
import { useCanvasStore } from "../store/canvas-store";

const PAN_SPEED_PX_PER_FRAME = 12;

export function useKeyboardPan(): void {
  const setView = useCanvasStore((s) => s.setView);

  useEffect(() => {
    const pressed = new Set<string>();
    let rafId: number | null = null;

    const tick = (): void => {
      const { view } = useCanvasStore.getState();
      let dx = 0;
      let dy = 0;
      if (pressed.has("w")) dy += PAN_SPEED_PX_PER_FRAME;
      if (pressed.has("s")) dy -= PAN_SPEED_PX_PER_FRAME;
      if (pressed.has("a")) dx += PAN_SPEED_PX_PER_FRAME;
      if (pressed.has("d")) dx -= PAN_SPEED_PX_PER_FRAME;
      if (dx !== 0 || dy !== 0) {
        setView({ ...view, panX: view.panX + dx, panY: view.panY + dy });
      }
      rafId = window.requestAnimationFrame(tick);
    };

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      if (isEditableTarget(event.target)) return;
      const key = event.key.toLowerCase();
      if (["w", "a", "s", "d"].includes(key)) {
        pressed.add(key);
        if (rafId === null) {
          rafId = window.requestAnimationFrame(tick);
        }
        event.preventDefault();
      }
    };

    const onKeyUp = (event: KeyboardEvent): void => {
      const key = event.key.toLowerCase();
      pressed.delete(key);
      if (pressed.size === 0 && rafId !== null) {
        window.cancelAnimationFrame(rafId);
        rafId = null;
      }
    };

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      if (rafId !== null) window.cancelAnimationFrame(rafId);
    };
  }, [setView]);
}

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName.toLowerCase();
  return tag === "input" || tag === "textarea" || tag === "select";
}
